import {
  AloftPrice,
  AloftTime,
  PairedSalesAnalysisFeature,
  PairedSalesStudy,
  StudiesSelectedPairs,
} from '@aloft/toolkit-shared';
import { Block, Button, Card, Checkbox, Flex, Icon, Typography } from '@aloft/toolkit-sui';

import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { selectDeviceWidth } from '../../../redux/features/selectors/uiSelectors.js';
import { useAppSelector } from '../../../redux/hooks.js';
import { ReconciliationStatistics } from '../../adjustPairedSalesStandalone/components/ReconciliationStatistics.js';
import { getIcon } from '../utils/getIcon.js';
import { makeAdjustResult } from '../utils/makeAdjustResult.js';
import { StudyCardPairsTable } from './StudyCardPairsTable.js';
import { useDeleteStudyModal } from './useDeleteStudyModal.js';

const AnimatedCollapseIcon = motion(Icon);
const AnimatedExpandedContentContainer = motion(Block);

export const StudyCard = ({
  id,
  title,
  feature,
  createdAt,
  isSelected,
  pairs,
  reconciledValue,
  notes,
  onCheckboxClick,
  onEditClick,
}: {
  id: string;
  title: string;
  feature: PairedSalesAnalysisFeature;
  createdAt: AloftTime;
  isSelected: boolean;
  pairs: StudiesSelectedPairs;
  reconciledValue: AloftPrice | number;
  notes: PairedSalesStudy['body']['notes'];
  onEditClick: () => void;
  onCheckboxClick: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const deviceWidth = useAppSelector(selectDeviceWidth);
  const [expanded, setExpanded] = useState(false);

  const results = pairs.map((p) => ({
    ...p,
    result: makeAdjustResult(p, reconciledValue, feature),
    feature,
  }));

  const { Modal: deleteStudyModal, openModal: openDeleteStudyModal } = useDeleteStudyModal(
    id,
    title,
  );

  return (
    <Card wrapperProps={{ className: 'studies-card' }} bodyProps={{ padding: 0 }}>
      <div
        className={`studies-card-content${!notes ? ' studies-card-content-no-notes' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="studies-export-checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox isChecked={isSelected} onChange={onCheckboxClick} />
        </div>
        <div className="studies-card-data-column first">
          <Block>
            <Flex alignItems="center" gap="0.5rem">
              <Icon icon={getIcon(feature)} size="lg" />
              <Typography variant="h2">{title}</Typography>
            </Flex>
          </Block>

          {deviceWidth !== 'phone' && <Block flexGrow={1} />}

          <Flex alignItems="center">
            <Typography variant="body1">
              {createdAt.formatTime(undefined, 'M/d/yy h:mm aa')}
            </Typography>
          </Flex>
        </div>
        <div className="studies-card-data-column">
          {deviceWidth !== 'phone' && (
            <Flex className="studies-card-actions" flexDirection="column" gap="0.5rem">
              <Flex
                className="studies-card-action-button-wrapper"
                onClick={(e) => e.stopPropagation()}
              >
                <Button fullWidth text="Edit" variant="secondary" onClick={onEditClick} size="sm" />
              </Flex>
              <Flex
                className="studies-card-action-button-wrapper"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  fullWidth
                  text="Delete"
                  variant="secondary"
                  onClick={openDeleteStudyModal}
                  size="sm"
                />
              </Flex>
            </Flex>
          )}
          {(deviceWidth !== 'phone' || !!notes) && (
            <AnimatedCollapseIcon
              animate={expanded ? 'expanded' : 'collapsed'}
              variants={{ collapsed: { rotateX: 0 }, expanded: { rotateX: 180 } }}
              transition={{ duration: 0.4 }}
              size="lg"
              icon={'arrow-up-s'}
            />
          )}
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <AnimatedExpandedContentContainer
            layout
            variants={{
              expanded: { height: 'auto', opacity: 1 },
              collapsed: { height: '0px', opacity: 0 },
            }}
            animate="expanded"
            initial="collapsed"
            exit="collapsed"
          >
            {deviceWidth !== 'phone' && (
              <div className="studies-card-pairs-table-container">
                <StudyCardPairsTable pairs={results} term={feature} />

                <Block paddingY=".5rem" paddingX="1rem">
                  <ReconciliationStatistics results={results.map((r) => r.result)} />
                </Block>
              </div>
            )}
            {!!notes && (
              <div className="studies-card-pairs-commentary-container">
                <Typography variant="h3">Commentary</Typography>
                <Typography>{notes}</Typography>
              </div>
            )}
          </AnimatedExpandedContentContainer>
        )}
      </AnimatePresence>
      {deleteStudyModal}
    </Card>
  );
};
